<template>
    <Modal close-esc-event="keydown" :close-button="false" modal-class="m-auto border-0" mask-class="grid" @opening="onOpen" @closing="onClose">
        <template #toggle="scope">
            <slot v-bind="scope" />
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body="scope">
            <div class="bg-white py-6 px-8 rounded-xl w-[480px] max-w-[90vw]">
                <h2 class="text-xl font-semibold mb-6">{{ $t('views.reports.selectFeedReconciliations.header') }}</h2>

                <div v-if="loading" class="flex justify-center py-8">
                    <Spinner class="w-10 h-10" :active="true"></Spinner>
                </div>

                <div v-else class="max-h-96 overflow-y-auto">
                    <table class="w-full text-sm border-collapse">
                        <thead>
                            <tr class="border-b border-gray-200">
                                <th class="w-8 py-2 pr-3"></th>
                                <th class="py-2 pr-4 text-left font-semibold text-gray-700">{{ $t('views.reports.selectFeedReconciliations.tableColumns.location') }}</th>
                                <th class="py-2 pr-4 text-left font-semibold text-gray-700">{{ $t('views.reports.selectFeedReconciliations.tableColumns.period') }}</th>
                                <th class="py-2 text-right font-semibold text-gray-700">{{ $t('views.reports.selectFeedReconciliations.tableColumns.amount') }}</th>
                                <th class="w-8 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="item in reconciliations"
                                :key="item.id"
                                :class="['border-b border-gray-100 cursor-pointer', isAutoDeselected(item.id) ? 'bg-yellow-50' : 'hover:bg-gray-50']"
                                @click="onToggle(item.id)"
                            >
                                <td class="py-2 pr-3">
                                    <input
                                        type="checkbox"
                                        class="w-4 h-4 accent-primary-800 cursor-pointer"
                                        :checked="localIds.includes(item.id)"
                                        @click.stop
                                        @change="onToggle(item.id)"
                                    />
                                </td>
                                <td class="py-2 pr-4 font-medium text-gray-900">{{ item.location.name }}</td>
                                <td class="py-2 pr-4 text-gray-500 tabular-nums">
                                    {{ formatDate(item.from_status_date) }} – {{ formatDate(item.to_status_date) }}
                                    <span v-if="isAutoDeselected(item.id)" class="ml-1 text-xs text-gray-400">{{ $t('views.reports.selectFeedReconciliations.autoDeselectedNote') }}</span>
                                </td>
                                <td class="py-2 text-right text-gray-700 tabular-nums">{{ formatAmount(item.amount_kg) }} kg</td>
                                <td class="py-2 pl-2">
                                    <EditReconciliationModal :reconciliation="item" @closing="onEditModalClose">
                                        <template #default="{ open }">
                                            <button type="button" class="p-1 rounded hover:bg-gray-100 transition-colors" @click.stop="open">
                                                <Icon class="h-3.5 w-3.5 opacity-50" :src="IconSource.Edit"></Icon>
                                            </button>
                                        </template>
                                    </EditReconciliationModal>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-4 text-xs text-gray-500">
                    <p>{{ $t('views.reports.selectFeedReconciliations.farmBarnMutualExclusionNote') }}</p>
                    <button v-if="!showDetail" type="button" class="text-primary-600 font-semibold underline mt-1 cursor-pointer hover:text-primary-800" @click="showDetail = true">
                        {{ $t('views.reports.selectFeedReconciliations.readMore') }}
                    </button>
                    <div v-if="showDetail" class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500 leading-relaxed">
                        {{ $t('views.reports.selectFeedReconciliations.farmBarnMutualExclusionDetail') }}
                    </div>
                </div>

                <div class="flex space-x-4 mt-6">
                    <CustomButton class="flex-1" color-preset="secondary" @click="scope.close">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.cancelButton') }}
                    </CustomButton>
                    <CustomButton class="flex-1" @click="onSave(scope.close)">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.saveButton') }}
                    </CustomButton>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import CustomButton from '@/components/ui/CustomButton.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import Spinner from '@/components/ui/Spinner.vue';
import Icon from '@/components/icons/Icon.vue';
import EditReconciliationModal from '@/views/dashboard/feed/feedReconciliations/editReconciliationModal/EditReconciliationModal.vue';
import { FeedReconciliation, IconSource } from '@/types';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/internationalization/i18n';

function isFarmLevel(r: FeedReconciliation): boolean {
    return store.state.directoryFarms.some((f) => f.id === r.location.id);
}

function getBarnFarmId(r: FeedReconciliation): number | null {
    const barn = store.state.directoryBarns.find((b) => b.id === r.location.id);
    return barn ? barn.farm_id : null;
}

function periodsOverlap(a: FeedReconciliation, b: FeedReconciliation): boolean {
    return a.from_status_date < b.to_status_date && b.from_status_date < a.to_status_date;
}

function farmBarnConflict(a: FeedReconciliation, b: FeedReconciliation): boolean {
    const aIsFarm = isFarmLevel(a);
    const bIsFarm = isFarmLevel(b);
    if (aIsFarm === bIsFarm) return false; // both farm or both barn — no conflict
    const farmRec = aIsFarm ? a : b;
    const barnRec = aIsFarm ? b : a;
    const barnFarmId = getBarnFarmId(barnRec);
    // A farm and a barn within it only truly conflict when their periods overlap.
    // Periods are half-open (from exclusive, to inclusive], so consecutive
    // reconciliations share only the boundary status date and may both be applied.
    return barnFarmId === farmRec.location.id && periodsOverlap(a, b);
}

function datesOverlap(a: FeedReconciliation, b: FeedReconciliation): boolean {
    return a.location.id === b.location.id && periodsOverlap(a, b);
}

function isWider(a: FeedReconciliation, b: FeedReconciliation): boolean {
    return a.from_status_date <= b.from_status_date && a.to_status_date >= b.to_status_date;
}

export default defineComponent({
    components: { Modal, CustomButton, CloseModalButton, Spinner, Icon, EditReconciliationModal },
    props: {
        reconciliationIds: {
            type: Array as () => number[],
            required: true,
        },
        applicableIds: {
            type: Array as () => number[],
            required: true,
        },
    },
    emits: ['update:reconciliationIds'],
    data() {
        return {
            loading: false,
            reconciliations: [] as FeedReconciliation[],
            localIds: [] as number[],
            initialAutoDeselected: new Set<number>(),
            recentlyAutoDeselected: new Set<number>(),
            autoDeselectTimers: [] as ReturnType<typeof setTimeout>[],
            showDetail: false,
            IconSource,
        };
    },
    computed: {
        reconciliationById(): Map<number, FeedReconciliation> {
            return new Map(this.reconciliations.map((r) => [r.id, r]));
        },
    },
    methods: {
        async onOpen() {
            this.localIds = [...this.$props.reconciliationIds];
            this.initialAutoDeselected = new Set();
            this.recentlyAutoDeselected = new Set();
            this.showDetail = false;
            // Ensure directory data is available for farm-barn conflict detection
            store.dispatch(ActionType.GetDirectoryFarms);
            store.dispatch(ActionType.GetDirectoryBarns);
            if (this.$props.applicableIds.length === 0) {
                this.reconciliations = [];
                return;
            }
            this.loading = true;
            try {
                this.reconciliations = await store.dispatch(ActionType.GetFeedReconciliationsByIds, {
                    ids: this.$props.applicableIds,
                });
            } finally {
                this.loading = false;
            }
            this.resolveInitialConflicts();
        },
        onClose() {
            this.autoDeselectTimers.forEach((t) => clearTimeout(t));
            this.autoDeselectTimers = [];
            this.reconciliations = [];
            this.localIds = [];
            this.initialAutoDeselected = new Set();
            this.recentlyAutoDeselected = new Set();
            this.showDetail = false;
        },
        resolveInitialConflicts() {
            const byId = this.reconciliationById;
            const selected = this.localIds.filter((id) => byId.has(id));
            const toRemove = new Set<number>();

            for (let i = 0; i < selected.length; i++) {
                for (let j = i + 1; j < selected.length; j++) {
                    const a = byId.get(selected[i]);
                    const b = byId.get(selected[j]);
                    if (!a || !b || (!datesOverlap(a, b) && !farmBarnConflict(a, b))) continue;

                    if (isWider(a, b)) {
                        // a contains b — remove the wider one (a)
                        toRemove.add(a.id);
                    } else if (isWider(b, a)) {
                        // b contains a — remove the wider one (b)
                        toRemove.add(b.id);
                    } else {
                        // partial overlap — remove both
                        toRemove.add(a.id);
                        toRemove.add(b.id);
                    }
                }
            }

            if (toRemove.size > 0) {
                this.localIds = this.localIds.filter((id) => !toRemove.has(id));
                this.initialAutoDeselected = toRemove;
            }
        },
        onToggle(id: number) {
            const index = this.localIds.indexOf(id);
            if (index === -1) {
                this.localIds = [...this.localIds, id];
                this.autoDeselectConflicts(id);
            } else {
                this.localIds = this.localIds.filter((i) => i !== id);
            }
        },
        autoDeselectConflicts(toggledId: number) {
            const byId = this.reconciliationById;
            const toggled = byId.get(toggledId);
            if (!toggled) return;

            const conflicting = this.localIds.filter((id) => {
                if (id === toggledId) return false;
                const other = byId.get(id);
                return other ? (datesOverlap(toggled, other) || farmBarnConflict(toggled, other)) : false;
            });

            if (conflicting.length === 0) return;

            this.localIds = this.localIds.filter((id) => !conflicting.includes(id));

            const newRecent = new Set(this.recentlyAutoDeselected);
            conflicting.forEach((id) => newRecent.add(id));
            this.recentlyAutoDeselected = newRecent;

            const timer = setTimeout(() => {
                const updated = new Set(this.recentlyAutoDeselected);
                conflicting.forEach((id) => updated.delete(id));
                this.recentlyAutoDeselected = updated;
                this.autoDeselectTimers = this.autoDeselectTimers.filter((t) => t !== timer);
            }, 3000);
            this.autoDeselectTimers.push(timer);
        },
        isAutoDeselected(id: number): boolean {
            return this.recentlyAutoDeselected.has(id) || this.initialAutoDeselected.has(id);
        },
        async onEditModalClose() {
            if (this.$props.applicableIds.length === 0) return;
            this.loading = true;
            try {
                const refreshed: FeedReconciliation[] = await store.dispatch(ActionType.GetFeedReconciliationsByIds, {
                    ids: this.$props.applicableIds,
                });
                this.reconciliations = refreshed;
                const existingIds = new Set(refreshed.map((r) => r.id));
                this.localIds = this.localIds.filter((id) => existingIds.has(id));
                this.initialAutoDeselected = new Set();
                this.resolveInitialConflicts();
            } finally {
                this.loading = false;
            }
        },
        onSave(closeModal: () => void) {
            this.$emit('update:reconciliationIds', [...this.localIds]);
            closeModal();
        },
        formatDate(dateString: string): string {
            return i18n.global.d(new Date(dateString), 'short');
        },
        formatAmount(amount: number): string {
            return i18n.global.n(amount, 'decimal');
        },
    },
});
</script>
